import { useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import InputMask from 'react-input-mask';
import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import businessDays from 'dayjs-business-days2';
import { DateRange, DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import OutlinedRadioControlLabel from '../../components/OutlinedRadioControlLabel';
import { companyIdSelector, isGTSelector } from '../../store/Slices/companySlice';
import { useStoreSelector } from '../../store/configstore';
import { useRqGetSponsorRemainingCount } from '../../react_query/sponsor_commercial/SponsorCommercialQuery';
import { ISponsorDetailsProp } from '../../dto/Sponsor.dto';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  validateName,
  validationNameType,
  ValidationResult,
} from '../../utils/validationName';
import {
  PlaceHolderContact,
  PlaceHolderEmail,
  PlaceHolderName,
  PlaceHolderPhone,
  sponsorDetailSchema,
} from '../../utils/sponsorValidation';
import sponsorConfig from '../../config/messageConfig';

type Props = {
  sponsorDetails: ISponsorDetailsProp;
  setSponsorDetails: Dispatch<SetStateAction<ISponsorDetailsProp>>;
  setSponsorDetailsValidate: (select: boolean) => void;
  dateRange: DateRange<Dayjs>;
  setDateRange: (value: DateRange<Dayjs>) => void;
  isContactSponsor: number;
  onContactSponsorChange: (val: number) => void;
  setAdspot: React.Dispatch<React.SetStateAction<number>>;
  adSpot: number;
  updateId: number | null;
};

dayjs.extend(businessDays);

const SponsorshipInformation = ({
  sponsorDetails,
  setSponsorDetails,
  setSponsorDetailsValidate,
  dateRange,
  setDateRange,
  isContactSponsor,
  onContactSponsorChange,
  setAdspot,
  adSpot,
  updateId,
}: Props) => {
  const companyId = useStoreSelector(companyIdSelector);
  const { data: remainingCount, isFetching } =
    useRqGetSponsorRemainingCount(companyId);
  const isGT = useSelector(isGTSelector);
  const [companyNameErrors, setCompanyNameErrors] = useState<string[]>([]);
  const [sponsorContactErrors, setSponsorContactErrors] = useState<string[]>(
    [],
  );
  const [sponsorEmailErrors, setSponsorEmailErrors] = useState<string[]>([]);
  const [sponsorPhoneErrors, setSponsorPhoneErrors] = useState<string[]>([]);

  const handleValidation = async (value: string, name: validationNameType) => {
    const validationResult: ValidationResult = await validateName(
      value,
      name as validationNameType,
    );
    if (name === 'sponsor_name') {
      setCompanyNameErrors(validationResult.errors);
    } else if (name === 'main_contact') {
      setSponsorContactErrors(validationResult.errors);
    } else if (name === 'email_address') {
      setSponsorEmailErrors(validationResult.errors);
    } else if (name === 'phone_number') {
      setSponsorPhoneErrors(validationResult.errors);
    }
  };

  const handleNameChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value, name } = e.target;

      handleValidation(value, name as validationNameType);

      setSponsorDetails((prevDetails: ISponsorDetailsProp) => {
        const updatedVal = {
          ...prevDetails,

          [name]: value,
        };

        return updatedVal;
      });
    },

    [setSponsorDetails],
  );

  const handleAdspotChange = (event: SelectChangeEvent<number>) => {
    setAdspot(Number(event.target.value));
  };

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      const updatedVal = {
        ...sponsorDetails,
        [name]: value,
      };

      setSponsorDetails(updatedVal);

      handleValidation(value, name as validationNameType);

      try {
        await sponsorDetailSchema.validate(updatedVal, { abortEarly: false });
        setSponsorDetailsValidate(true);
      } catch {
        setSponsorDetailsValidate(false);
      }
    },
    [sponsorDetails, setSponsorDetails, setSponsorDetailsValidate],
  );

  const hasEmptyFields = useCallback(() => {
    return !(
      sponsorDetails.sponsor_name.length &&
      sponsorDetails.main_contact &&
      sponsorDetails.email_address &&
      sponsorDetails.phone_number
    );
  }, [
    sponsorDetails.email_address,
    sponsorDetails.main_contact,
    sponsorDetails.phone_number,
    sponsorDetails.sponsor_name.length,
  ]);

  useEffect(() => {
    const isContainsError =
      companyNameErrors.length > 0 ||
      sponsorContactErrors.length > 0 ||
      hasEmptyFields();
    setSponsorDetailsValidate(!isContainsError);
  }, [
    companyNameErrors,
    sponsorContactErrors,
    setSponsorDetailsValidate,
    hasEmptyFields,
  ]);

  useEffect(() => {
    if (remainingCount?.remaining && !isFetching) {
      setAdspot(pre => {
        return pre > remainingCount.remaining ? 1 : pre;
      });
    }
  }, [isFetching, remainingCount, setAdspot]);

  return (
    <>
      <Typography
        variant='h5'
        aria-level={2}
        className='text-white font-bold mb-8'>
        Enter Sponsor Details
      </Typography>
      <Grid container>
        <Stack spacing={3} direction='row' className='w-full'>
          <Grid item xs={6}>
            <TextField
              required
              id='sponsor-name'
              name='sponsor_name'
              value={sponsorDetails.sponsor_name}
              onChange={handleNameChange}
              placeholder='Sponsor/Company Name*'
              helperText={
                companyNameErrors.length > 0
                  ? companyNameErrors.join(' ')
                  : PlaceHolderName
              }
              error={companyNameErrors.length > 0}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              id='main-contact'
              name='main_contact'
              value={sponsorDetails.main_contact}
              onChange={handleNameChange}
              placeholder="Sponsor's Main Contact*"
              helperText={
                sponsorContactErrors.length > 0
                  ? sponsorContactErrors.join(' ')
                  : PlaceHolderContact
              }
              error={sponsorContactErrors.length > 0}
            />
          </Grid>
        </Stack>
        <Stack spacing={3} direction='row' className='w-full mb-10'>
          <Grid item xs={6}>
            <TextField
              required
              id='main-email'
              name='email_address'
              value={sponsorDetails.email_address}
              onChange={handleChange}
              placeholder="Main Contact's Email Address"
              helperText={
                sponsorEmailErrors.length > 0
                  ? sponsorEmailErrors.join(' ')
                  : PlaceHolderEmail
              }
              error={sponsorEmailErrors.length > 0}
            />
          </Grid>
          <Grid item xs={6}>
            <InputMask
              mask={PlaceHolderPhone}
              disabled={false}
              value={sponsorDetails.phone_number}
              onChange={handleChange}>
              <TextField
                required
                disabled={false}
                type='text'
                name='phone_number'
                placeholder="Main Contact's Phone Number"
                id='mobile_number'
                helperText={
                  sponsorPhoneErrors.length > 0
                    ? sponsorPhoneErrors.join(' ')
                    : PlaceHolderPhone
                }
                error={sponsorPhoneErrors.length > 0}
              />
            </InputMask>
          </Grid>
        </Stack>
        <Stack
          spacing={3}
          direction='row'
          className='w-full items-center mb-10'>
          <Grid item xs={6}>
            <Box>
              <Typography variant='subtitle1' className='text-white-50 mb-2'>
                How long does this sponsorship run?
              </Typography>
            </Box>
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              minDate={dayjs()}
            />
          </Grid>
          <Grid item xs={6}>
            {isGT && (
              <FormControl sx={{ width: 300 }}>
                <Typography variant='subtitle1' className='text-white-50 mb-2'>
                  How Many ad spots per event ?
                </Typography>
                {remainingCount?.remaining && !isFetching && (
                  <Select
                    labelId='adspot-select-label'
                    id='adspot-select'
                    placeholder='ad spot'
                    value={adSpot}
                    className='radius-100 blue-variant4'
                    onChange={handleAdspotChange}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      PaperProps: {
                        className: 'select-menuitem-secondary',
                      },
                    }}
                    inputProps={{
                      style: {
                        height: '40px !important',
                        borderRadius: '100% !important',
                      },
                    }}>
                    {[...Array(Math.min(remainingCount.remaining, 4))].map(
                      (_, index) => (
                        <MenuItem key={index + 1} value={index + 1}>
                          {`${index + 1} ad spot${index === 3 ? ' (Title Sponsor)' : 's'
                            } per event`}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                )}
              </FormControl>
            )}
          </Grid>
        </Stack>
        <Grid item xs={12}>
          <RadioGroup
            name='is_contact_sponsor'
            value={isContactSponsor}
            onChange={event => onContactSponsorChange(+event.target.value)}>
            {updateId && (
              <OutlinedRadioControlLabel
                value='2'
                label={sponsorConfig.oldSponsorInfo}
              />
            )}

            <OutlinedRadioControlLabel
              value='0'
              label={sponsorConfig.infoFromPartner}
            />

            <OutlinedRadioControlLabel
              value='1'
              label={sponsorConfig.infoFromSponsor}
            />
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid container spacing={3} direction='row' className='w-full mb-10'>
        <Grid item xs={6}>
          <Typography
            component='label'
            htmlFor='main-email'
            variant='body2'
            sx={{ display: 'block', mb: 0.5 }}>
            Main Contact's Email Address *
          </Typography>
          <TextField
            required
            id='main-email'
            name='email_address'
            value={sponsorDetails.email_address}
            onChange={handleChange}
            helperText={
              sponsorEmailErrors.length > 0
                ? sponsorEmailErrors.join(' ')
                : PlaceHolderEmail
            }
            error={sponsorEmailErrors.length > 0}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            component='label'
            htmlFor='mobile_number'
            variant='body2'
            sx={{ display: 'block', mb: 0.5 }}>
            Main Contact's Phone Number *
          </Typography>
          <InputMask
            mask={PlaceHolderPhone}
            disabled={false}
            value={sponsorDetails.phone_number}
            onChange={handleChange}>
            <TextField
              required
              disabled={false}
              type='text'
              name='phone_number'
              id='mobile_number'
              helperText={
                sponsorPhoneErrors.length > 0
                  ? sponsorPhoneErrors.join(' ')
                  : PlaceHolderPhone
              }
              error={sponsorPhoneErrors.length > 0}
            />
          </InputMask>
        </Grid>
      </Grid>
      <Grid container spacing={3} direction='row' className='w-full mb-10'>
        <Grid item xs={6}>
          <Typography
            variant='subtitle1'
            aria-level={2}
            className='text-white-50 mb-2'>
            How long does this sponsorship run?
          </Typography>

          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            minDate={dayjs()}
            slots={{ field: MultiInputDateRangeField }}
            slotProps={{
              textField: {
                InputLabelProps: {
                  shrink: false,
                },
                label: '',
              },
            }}
          />
        </Grid>
        <Grid item xs={6}>
          {isGT && (
            <FormControl sx={{ width: 300 }}>
              <Typography
                variant='subtitle1'
                aria-level={2}
                className='text-white-50 mb-2'
                id='adspot-select-label'>
                How Many ad spots per event ?
              </Typography>
              {remainingCount?.remaining && !isFetching && (
                <Select
                  labelId='adspot-select-label'
                  id='adspot-select'
                  placeholder='ad spot'
                  value={adSpot}
                  className='radius-100 blue-variant4'
                  onChange={handleAdspotChange}
                  SelectDisplayProps={{ 'aria-controls': undefined }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: {
                      className: 'select-menuitem-secondary',
                    },
                  }}
                  inputProps={{
                    style: {
                      height: '40px !important',
                      borderRadius: '100% !important',
                    },
                  }}>
                  {[...Array(Math.min(remainingCount.remaining || 0, 4))].map(
                    (_, index) => (
                      <MenuItem key={index + 1} value={index + 1}>
                        {`${index + 1} ad spot${index === 3 ? ' (Title Sponsor)' : 's'
                          } per event`}
                      </MenuItem>
                    ),
                  )}
                </Select>
              )}
            </FormControl>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <RadioGroup
          name='is_contact_sponsor'
          value={isContactSponsor}
          onChange={event => onContactSponsorChange(+event.target.value)}>
          {updateId && (
            <OutlinedRadioControlLabel
              value='2'
              label={sponsorConfig.oldSponsorInfo}
            />
          )}

          <OutlinedRadioControlLabel
            value='0'
            label={sponsorConfig.infoFromPartner}
          />

          <OutlinedRadioControlLabel
            value='1'
            label={sponsorConfig.infoFromSponsor}
          />
        </RadioGroup>
      </Grid>
      {/* </Grid> */}
    </>
  );
};
export default SponsorshipInformation;
